import {DagreNodesOnlySettings, Orientation} from '../../models/graph-kn.cm-api-models';
import { Edge, Graph, Layout } from '../../models/graph-kn.model-interfaces';
import * as dagre from 'dagre';

export enum Alignment {
    CENTER = 'C',
    UP_LEFT = 'UL',
    UP_RIGHT = 'UR',
    DOWN_LEFT = 'DL',
    DOWN_RIGHT = 'DR'
}
const DEFAULT_EDGE_NAME = '\x00';
const EDGE_KEY_DELIM = '\x01';
export interface DagreSettings {
    orientation?: Orientation;
    marginX?: number;
    marginY?: number;
    edgePadding?: number;
    rankPadding?: number;
    nodePadding?: number;
    align?: Alignment;
    acyclicer?: 'greedy' | undefined;
    ranker?: 'network-simplex' | 'tight-tree' | 'longest-path'|'layered'|'mrtree';
    multigraph?: boolean;
    compound?: boolean;
}
export class DagreNodesOnlyLayout implements Layout {
    defaultSettings: DagreNodesOnlySettings = {
        orientation: Orientation.LEFT_TO_RIGHT,
        marginX: 0,
        marginY: 0,
        edgePadding: 60,
        rankPadding: 50,
        nodePadding: 20,
        curveDistance: 20,
       ranker:'tight-tree',
        multigraph: true,
        compound: true
    };
    settings: DagreNodesOnlySettings = {};

    dagreGraph: any;
    dagreNodes: any;
    dagreEdges: any;

    run(graph: Graph): Graph {
        this.createDagreGraph(graph);
        dagre.layout(this.dagreGraph);
        graph.edgeLabels = this.dagreGraph._edgeLabels;

        for (const dagreNodeId in this.dagreGraph._nodes) {
            const dagreNode = this.dagreGraph._nodes[dagreNodeId];

            const node = graph.nodes.find(n => n.id === dagreNode.id);
            node!.position = {
                x: dagreNode.x,
                y: dagreNode.y
            };
            node!.dimension = {
                width: dagreNode.width,
                height: dagreNode.height
            };
        }
        for (const edge of graph.edges) {
            this.updateEdge(graph, edge);
        }

        return graph;
    }

    updateEdge(graph: Graph, edge: Edge): Graph {
        const sourceNode = graph.nodes.find(n => n.id === edge.source)!;
        const targetNode = graph.nodes.find(n => n.id === edge.target)!;
        const rankAxis: 'x' | 'y' = this.settings.orientation === 'BT' || this.settings.orientation === 'TB' ? 'y' : 'x';
        const orderAxis: 'x' | 'y' = rankAxis === 'y' ? 'x' : 'y';
        const rankDimension = rankAxis === 'y' ? 'height' : 'width';

        // determine new arrow position
        const dir = sourceNode.position![rankAxis] <= targetNode.position![rankAxis] ? -1 : 1;
        const startingPoint = {
            [orderAxis]: sourceNode.position![orderAxis],
            [rankAxis]: sourceNode.position![rankAxis] - dir * (sourceNode.dimension![rankDimension] / 2)
        };
        const endingPoint = {
            [orderAxis]: targetNode.position![orderAxis],
            [rankAxis]: targetNode.position![rankAxis] + dir * (targetNode.dimension![rankDimension] / 2)
        };

        const curveDistance = this.settings.curveDistance || this.defaultSettings.curveDistance;
        edge.points = [
            startingPoint,
            {
                [orderAxis]: startingPoint[orderAxis],
                [rankAxis]: startingPoint[rankAxis] - dir * (curveDistance || 1)
            },
            {
                [orderAxis]: endingPoint[orderAxis],
                [rankAxis]: endingPoint[rankAxis] + dir * (curveDistance || 1)
            },
            endingPoint
        ];
        const edgeLabelId = `${edge.source}${EDGE_KEY_DELIM}${edge.target}${EDGE_KEY_DELIM}${DEFAULT_EDGE_NAME}`;
        const matchingEdgeLabel = graph.edgeLabels[edgeLabelId];
        if (matchingEdgeLabel) {
            matchingEdgeLabel.points = edge.points;
        }
        return graph;
    }

    createDagreGraph(graph: Graph): any {
        const settings = Object.assign({}, this.defaultSettings, this.settings);
        this.dagreGraph = new dagre.graphlib.Graph({compound: settings.compound, multigraph: settings.multigraph});
        this.dagreGraph.setGraph({
            rankdir: settings.orientation,
            marginx: settings.marginX,
            marginy: settings.marginY,
            edgesep: settings.edgePadding,
            ranksep: settings.rankPadding,
            nodesep: settings.nodePadding,
            align: settings.align,
            acyclicer: settings.acyclicer,
            ranker: settings.ranker,
            multigraph: settings.multigraph,
            compound: settings.compound
        });

        // Default to assigning a new object as a label for each new edge.
        this.dagreGraph.setDefaultEdgeLabel(() => {
            return {
                /* empty */
            };
        });
        this.dagreNodes = graph.nodes.map(n => {
            const node: any = Object.assign({}, n)!;
            node.width = n.dimension!.width;
            node.height = n.dimension!.height;
            node.x = n.position!.x;
            node.y = n.position!.y;
            return node;
        });

        this.dagreEdges = graph.edges.map(l => {
            const newLink: any = Object.assign({}, l);
            if (!newLink.id) {
                newLink.id = id();
            }
            return newLink;
        });

        for (const node of this.dagreNodes) {
            if (!node.width) {
                node.width = 20;
            }
            if (!node.height) {
                node.height = 30;
            }

            // update dagre
            this.dagreGraph.setNode(node.id, node);
        }

        // update dagre
        for (const edge of this.dagreEdges) {
            if (settings.multigraph) {
                this.dagreGraph.setEdge(edge.source, edge.target, edge, edge.id);
            } else {
                this.dagreGraph.setEdge(edge.source, edge.target);
            }
        }

        Object.entries(this.dagreGraph._nodes).forEach(n => (n[1] as {
            id: string
        })?.id ? null : delete this.dagreGraph._nodes[n[0]])
        return this.dagreGraph;
    }
}
const cache: Record<string, boolean> = {};
 export function id(): string {
    let newId = ('0000' + ((Math.random() * Math.pow(36, 4)) << 0).toString(36)).slice(-4);
    newId = `a${newId}`;
    if (!cache[newId]) {
        cache[newId] = true;
        return newId;
    }
    return id();
}
